import { useTranslation } from 'react-i18next';
import type { SensorBinding, SensorStatus, ValueSource } from '@atnt/shared';
import type { SensorDto } from '../../api';
import { bindingDecimals, bindingUnit, sourceSensorId } from '../bindings';
import { formatNumber, NO_VALUE, withUnit } from './format';
import { useSensorMeta, useSensorValue, type SensorReading } from './LiveProvider';

/** A value ready to render: number text, unit and status colour. */
export interface DisplayValue {
  /** "854.6 m", "0.4°C", manual text as typed, or "—" */
  text: string;
  /** number part only ("854.6") */
  number: string;
  unit: string;
  status: SensorStatus;
  reading: SensorReading | null;
}

/** Current UI language (`uz-Latn`, `ru`, `en`) for number/date formatting. */
export function useLocale(): string {
  return useTranslation().i18n.language || 'uz-Latn';
}

export function formatBinding(binding: SensorBinding, reading: SensorReading | undefined, meta: SensorDto | undefined, locale: string, unitOverride?: string): DisplayValue {
  const unit = unitOverride || bindingUnit(binding, meta);
  const number = formatNumber(reading?.value, { decimals: bindingDecimals(binding, meta), locale });
  return { text: withUnit(number, unit), number, unit, status: reading?.status ?? 'unknown', reading: reading ?? null };
}

export function formatManual(value: string): DisplayValue {
  const text = value.trim() || NO_VALUE;
  return { text, number: text, unit: '', status: 'unknown', reading: null };
}

/** Live (sensor) or manual value of a `ValueSource` — indicator widgets, label rows, table cells. */
export function useValueSource(source: ValueSource | null | undefined, unitOverride?: string): DisplayValue {
  const locale = useLocale();
  const sensorId = sourceSensorId(source);
  const reading = useSensorValue(sensorId);
  const meta = useSensorMeta(sensorId);
  if (!source) return formatManual('');
  if (source.kind === 'manual') return formatManual(source.value);
  return formatBinding(source.binding, reading, meta, locale, unitOverride);
}
